import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Vehicle, VehicleEvent } from '@msc-stock/shared';
import { api } from '../lib/api.js';
import { formatDate, useI18n, yen } from '../i18n.js';
import { Badge, Card, DivisionBadge, Empty, Field, Notice } from '../components/ui.js';

/** 履歴の種類を日本語にする。 */
const EVENT_LABEL: Record<string, string> = {
  added: '在庫に入った',
  sold: '売却 (在庫リストから消えた)',
  returned: '在庫に戻った',
  updated: '内容が変わった',
  sale_recorded: '売却情報を入力',
  edited: '手で修正',
};

export function VehicleDetailPage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams();
  const vehicleId = Number(id);

  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [events, setEvents] = useState<VehicleEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  // 売却情報のフォーム
  const [salePrice, setSalePrice] = useState('');
  const [saleDate, setSaleDate] = useState('');
  const [buyer, setBuyer] = useState('');
  const [saleNote, setSaleNote] = useState('');

  useEffect(() => {
    if (!Number.isFinite(vehicleId)) return;
    api
      .vehicle(vehicleId)
      .then((r) => {
        setVehicle(r.vehicle);
        setEvents(r.events);
        setSalePrice(r.vehicle.salePrice == null ? '' : String(r.vehicle.salePrice));
        setSaleDate(r.vehicle.saleDate ?? '');
        setBuyer(r.vehicle.buyer ?? '');
        setSaleNote(r.vehicle.saleNote ?? '');
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [vehicleId]);

  async function saveSale(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const r = await api.saveSale(vehicleId, {
        salePrice: salePrice.trim() === '' ? null : Math.round(Number(salePrice)),
        saleDate: saleDate || null,
        buyer: buyer.trim() || null,
        saleNote: saleNote.trim() || null,
      });
      setVehicle(r.vehicle);
      setSaved(true);
      const fresh = await api.vehicle(vehicleId);
      setEvents(fresh.events);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function toggleStatus() {
    if (!vehicle) return;
    setBusy(true);
    try {
      const next = vehicle.status === 'sold' ? 'in_stock' : 'sold';
      const r = await api.setStatus(vehicleId, { status: next });
      setVehicle(r.vehicle);
      const fresh = await api.vehicle(vehicleId);
      setEvents(fresh.events);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (error && !vehicle) return <Notice kind="bad">{error}</Notice>;
  if (!vehicle) return <Empty>{t('loading')}</Empty>;

  const profit = vehicle.salePrice == null ? null : vehicle.salePrice - vehicle.purchasePrice;

  return (
    <>
      <div className="row" style={{ marginBottom: 10 }}>
        <button type="button" className="btn small" onClick={() => navigate(-1)}>
          ← {t('back')}
        </button>
      </div>

      <h1 className="page-title">
        <DivisionBadge division={vehicle.division} /> {vehicle.maker} {vehicle.model}
      </h1>
      <p className="page-sub">
        <span style={{ fontFamily: 'ui-monospace, monospace' }}>
          {vehicle.chassisMissing ? `${t('chassis')}: —` : vehicle.chassisRaw}
        </span>
        {' · '}
        {vehicle.status === 'sold' ? <Badge kind="neutral">{t('soldStatus')}</Badge> : <Badge kind="ok">{t('inStock')}</Badge>}
        {vehicle.condition === 'flood' && (
          <>
            {' '}
            <Badge kind="warn">{t('flood')}</Badge>
          </>
        )}
        {vehicle.partsFlag && (
          <>
            {' '}
            <Badge kind="neutral">{t('parts')}</Badge>
          </>
        )}
      </p>

      {error && <Notice kind="bad">{error}</Notice>}
      {saved && <Notice kind="ok">{t('saved')}</Notice>}

      <div className="grid cols-2">
        <Card title={t('detail')}>
          <dl className="kv">
            <dt>{t('maker')}</dt>
            <dd>{vehicle.maker || '—'}</dd>
            <dt>{t('model')}</dt>
            <dd>{vehicle.model || '—'}</dd>
            <dt>{t('grade')}</dt>
            <dd>{vehicle.grade || '—'}</dd>
            <dt>{t('color')}</dt>
            <dd>{vehicle.color || '—'}</dd>
            <dt>{t('year')}</dt>
            <dd>
              {vehicle.yearRaw || '—'}
              {vehicle.yearMonth && <span className="faint"> ({vehicle.yearMonth})</span>}
            </dd>
            <dt>{t('supplier')}</dt>
            <dd>
              {vehicle.supplier ? (
                <Link to={`/stock?supplier=${encodeURIComponent(vehicle.supplier)}`}>{vehicle.supplier}</Link>
              ) : (
                '—'
              )}
            </dd>
            <dt>{t('lotNo')}</dt>
            <dd>{vehicle.lotNo || '—'}</dd>
            <dt>{t('purchaseDate')}</dt>
            <dd>{formatDate(vehicle.purchaseDate, lang)}</dd>
            <dt>{t('purchasePrice')}</dt>
            <dd>{yen(vehicle.purchasePrice)}</dd>
            {vehicle.status === 'sold' && (
              <>
                <dt>{t('soldDate')}</dt>
                <dd>{formatDate(vehicle.soldDate, lang)}</dd>
              </>
            )}
          </dl>

          <div className="row" style={{ marginTop: 14 }}>
            <button type="button" className="btn small" onClick={toggleStatus} disabled={busy}>
              {vehicle.status === 'sold' ? t('markInStock') : t('markSold')}
            </button>
            <span className="faint">{t('manualStatusHint')}</span>
          </div>
        </Card>

        <Card title={t('salePrice')}>
          <form onSubmit={saveSale}>
            <div className="grid cols-2" style={{ gap: 10 }}>
              <Field label={t('salePrice')}>
                <input
                  type="number"
                  inputMode="numeric"
                  value={salePrice}
                  placeholder={t('notEntered')}
                  onChange={(e) => setSalePrice(e.target.value)}
                />
              </Field>
              <Field label={t('saleDate')}>
                <input type="date" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} />
              </Field>
            </div>
            <div style={{ marginTop: 10 }}>
              <Field label={t('buyer')}>
                <input type="text" value={buyer} onChange={(e) => setBuyer(e.target.value)} />
              </Field>
            </div>
            <div style={{ marginTop: 10 }}>
              <Field label={t('saleNote')}>
                <textarea rows={3} value={saleNote} onChange={(e) => setSaleNote(e.target.value)} />
              </Field>
            </div>

            {profit != null && (
              <p style={{ marginTop: 12, marginBottom: 0 }}>
                {t('profit')}:{' '}
                <strong style={{ color: profit >= 0 ? 'var(--ok)' : 'var(--bad)' }}>{yen(profit)}</strong>{' '}
                <span className="faint">
                  ({yen(vehicle.salePrice)} − {yen(vehicle.purchasePrice)})
                </span>
              </p>
            )}

            <button type="submit" className="btn primary" style={{ marginTop: 14 }} disabled={busy}>
              {busy ? t('loading') : t('save')}
            </button>
          </form>
        </Card>
      </div>

      <div style={{ marginTop: 16 }}>
        <Card title={t('timeline')}>
          {events.length === 0 ? (
            <div className="faint">—</div>
          ) : (
            <ul className="timeline">
              {events.map((ev) => (
                <li key={ev.id}>
                  <div className="when">{new Date(ev.createdAt).toLocaleString(lang === 'ja' ? 'ja-JP' : 'en-GB')}</div>
                  <div>{EVENT_LABEL[ev.type] ?? ev.type}</div>
                  {ev.detail && <EventDetail detail={ev.detail} />}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

/** 履歴の JSON を読みやすく出す。変更内容は「旧 → 新」で並べる。 */
function EventDetail({ detail }: { detail: string }) {
  let parsed: unknown;
  try {
    parsed = JSON.parse(detail);
  } catch {
    return <div className="faint">{detail}</div>;
  }

  const obj = parsed as { changes?: Record<string, [string, string]> } | null;
  if (obj?.changes) {
    return (
      <ul className="faint" style={{ margin: '4px 0 0', paddingLeft: 16 }}>
        {Object.entries(obj.changes).map(([field, [before, after]]) => (
          <li key={field}>
            {field}: {before || '—'} → {after || '—'}
          </li>
        ))}
      </ul>
    );
  }
  return null;
}
